import React, { useState, useCallback } from 'react';
import { useSearchJobTitles, useSaveJobTitle } from '../../../apis/jobTitles';
import type { JobTitle as JobTitleType, JobTitlesSearch } from '../../../apis/jobTitles/jobTitles.types';
import { MutationResponse } from '../../../apis/shared.types';
import JobTitleForm from './taskBaselineJobTitleForm';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import Drawer from '@Common/Drawer/Drawer';
import Icon from '@Common/Icon';
import Notification from '@Common/Notification';
import { getDataTableSortFields, getSearchParamSortFields } from '../../../helpers/sortHelper';
import { getNullableOnChangeValue } from '../../../helpers/multiselect';
import LoadingIndicator from '@Components/Common/LoadingIndicator/LoadingIndicator';

type JobTitleProps = {
    subscriptionId: number;
    onClose: () => void;
}

function JobTitle(props: JobTitleProps) {
    const newJobTitleObj: JobTitleType = {
        id: 0,
        name: "",
        isActive: true,
        subscriptionId: props.subscriptionId,
        createdByUserId: 0,
        createdDate: new Date(),
    };

    const [selectedJobTitle, setSelectedJobTitle] = useState<JobTitleType | null>(newJobTitleObj);
    const [editJobTitle, setEditJobTitle] = useState<JobTitleType>(newJobTitleObj);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isFormValid, setIsFormValid] = useState(true);
    const [searchName, setSearchName] = useState("");
    const [searchParams, setSearchParams] = useState<JobTitlesSearch>({
        subscriptionId: props.subscriptionId,
        isActive: true,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: "name",
        sortDir: "asc",
        name: ""
    });

    // Define the callback for after data is saved
    const handleDataFetched = useCallback((response: MutationResponse) => {
        Notification("success", "Save Successful", "Job Title saved successfully");
        setDrawerOpen(false);
        setSearchParams(sp => ({ ...sp, pageNumber: 1 }));
    }, []); // Empty dependency array means the function is created once

    const { data, refetch } = useSearchJobTitles(searchParams);
    const { mutateAsync, isPending } = useSaveJobTitle(handleDataFetched);

    // Save function
    const save = async () => {
        await mutateAsync(editJobTitle);
    };

    // Name filter handlers
    const filterNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchName(e.target.value);
    };

    const keyUpName = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearchBlur();
        }
    };

    const onSearchBlur = () => {
        setSearchParams(sp => ({ ...sp, name: searchName }));
    };

    // Active filter
    const onActiveChange = (e) => {
        const selectedValue = getNullableOnChangeValue(e);
        setSearchParams(sp => ({ ...sp, isActive: selectedValue }));
    };

    // Pagination
    const onPageChange = (e) => {
        setSearchParams(sp => ({ ...sp, pageNumber: (e.page + 1) }));
    };

    // Add new
    const onAddNew = () => {
        setSelectedJobTitle(null);
        setEditJobTitle(newJobTitleObj);
        setDrawerOpen(true);
        setIsFormValid(true);
    };

    // Form change
    const onFormChange = (name, value) => {
        setEditJobTitle(job => ({ ...job, [name]: value }));
    };

    // Drawer handling
    const saveClick = () => {
        if (editJobTitle.name.trim().length < 1) {
            setIsFormValid(false);
            return;
        }
        setIsFormValid(true);
        save();
    };

    const cancelClick = () => {
        setDrawerOpen(false);
    };

    // Selection
    const selectJobTitle = (e) => {
        const newValue = e.value || selectedJobTitle;
        setSelectedJobTitle(newValue);
        setEditJobTitle(newValue);
        setDrawerOpen(true);
        setIsFormValid(true);
    };

    // Table Action Template
    const actionTemplate = (rowData) => {
        if (rowData.isActive) {
            return <div className="text-success text-center" > <Icon name="check-circle" /> </div>;
        } else {
            return <div className="text-danger text-center" > <Icon name="times-circle" /> </div>;
        }
    };

    // Sorting
    const onSort = ({ sortField, sortOrder }) => {
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
        if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
            setSearchParams(sp => ({
                ...sp,
                sortBy,
                sortDir
            }));
        }
    };

    // Dropdowns and filters
    const activeOptions = [
        { label: "Yes", value: true },
        { label: "No", value: false },
        { label: "All", value: null }
    ];

    const activeFilter = (
        <Dropdown
            className="ui-column-filter"
            value={searchParams.isActive}
            options={activeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={onActiveChange}
            placeholder="Select an option"
        />
    );

    const nameFilterElement = (
        <input
            type="text"
            className="p-inputtext"
            placeholder="Filter by Name"
            size={50}
            onBlur={onSearchBlur}
            onChange={filterNameChange}
            onKeyUp={keyUpName}
            value={searchName}
        />
    );

    const { sortField, sortOrder } = getDataTableSortFields(searchParams);

    return (
        <div>
            {
                drawerOpen &&
                <Drawer isWide={false} title="Job Title" onSaveClick={saveClick} onCancelClick={cancelClick}>
                    <LoadingIndicator isLoading={isPending} hideWhileLoading={true}>
                        <JobTitleForm
                            jobTitle={editJobTitle}
                            onFormChange={onFormChange}
                            isFormValid={isFormValid}
                            onSubmit={saveClick} />
                    </LoadingIndicator>
                </Drawer>
            }

            <div>
                <div className="identifier-header">
                    <div className="title">Job Title</div>
                    <div className="close">
                        <button type="button" onClick={props.onClose} aria-label="close popup"><Icon name="times" /></button>
                    </div>
                </div>

                <div className="identifier-add-new">
                    <div className="col-sm-12">
                        <button type="button" className="btn btn-primary float-end" onClick={onAddNew} aria-label="add new job title">
                            <span className="me-2">Add New</span><Icon name="plus-circle" />
                        </button>
                    </div>
                </div>

                <div className="identifier-results-table">
                    <DataTable
                        filterDisplay="row"
                        className="p-datatable-sm p-datatable-striped"
                        value={data.results}
                        selectionMode="single"
                        selection={selectedJobTitle}
                        onSelectionChange={selectJobTitle}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                    >
                        <Column field="name" header="Name" sortable={true} filter={true} filterElement={nameFilterElement} />
                        <Column field="isActive" header="Active" sortable={true} body={actionTemplate} style={{ width: '120px' }} filter={true} filterElement={activeFilter} />
                    </DataTable>
                    {
                        data.totalCount > searchParams.pageSize &&
                        <Paginator
                            rows={searchParams.pageSize}
                            totalRecords={data.totalCount}
                            first={(searchParams.pageNumber - 1) * searchParams.pageSize
                            }
                            onPageChange={onPageChange} />
                    }
                    <div className="results-count">Total records: {data.totalCount}</div>
                </div>
            </div>
        </div>
    );
}

export default JobTitle;
